// M2V.io - Projects Store
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api'

export interface Scene {
  id: number
  idx: number
  start_time: number
  duration_s: number
  description: string | null
  image_prompt: string | null
  video_prompt: string | null
  status: string
  version: number
  image_url: string | null
  video_url: string | null
}

export interface Project {
  id: number
  user_id: number
  title: string
  language: string
  format: 'landscape' | 'portrait' | 'square'
  visual_style: string | null
  main_character_gender: string | null
  is_public: boolean
  public_status: string
  mode: 'auto' | 'manual'
  status: string
  current_step: number
  error_message: string | null
  credits_reserved: number
  credits_used: number
  image_regenerations_count: number
  video_regenerations_count: number
  created_at: string
  updated_at: string
  completed_at: string | null
  scenes: Scene[]
  progress: number
  final_video_url: string | null
  thumbnail_url: string | null
}

export const useProjectsStore = defineStore('projects', () => {
  const projects = ref<Project[]>([])
  const currentProject = ref<Project | null>(null)
  const loading = ref(false)
  const total = ref(0)
  const page = ref(1)

  const activeProjects = computed(() => 
    projects.value.filter(p => 
      ['pending', 'processing', 'script_ready', 'images_ready', 'videos_ready'].includes(p.status)
    )
  )

  const completedProjects = computed(() =>
    projects.value.filter(p => p.status === 'completed')
  )

  // Check if user has an active project
  async function checkHasActiveProject(): Promise<{ hasActive: boolean; activeProject: any | null }> {
    try {
      const response = await api.get('/projects/has-active')
      return {
        hasActive: response.data.has_active,
        activeProject: response.data.active_project
      }
    } catch {
      return { hasActive: false, activeProject: null }
    }
  }

  // Fetch user's projects
  async function fetchProjects(pageNum = 1, pageSize = 20) {
    loading.value = true
    try {
      const response = await api.get('/projects', {
        params: { page: pageNum, page_size: pageSize }
      })
      projects.value = response.data.items
      total.value = response.data.total
      page.value = pageNum
    } finally {
      loading.value = false
    }
  }

  // Fetch single project
  async function fetchProject(projectId: number) {
    loading.value = true
    try {
      const response = await api.get(`/projects/${projectId}`)
      currentProject.value = response.data
      
      // Update in list if exists
      const idx = projects.value.findIndex(p => p.id === projectId)
      if (idx >= 0) {
        projects.value[idx] = response.data
      }
      
      return response.data
    } finally {
      loading.value = false
    }
  }

  // Create project
  async function createProject(formData: FormData) {
    loading.value = true
    try {
      const response = await api.post('/projects', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      const project = response.data
      projects.value.unshift(project)
      return project
    } finally {
      loading.value = false
    }
  }

  // Start project generation
  async function startProject(projectId: number) {
    loading.value = true
    try {
      const response = await api.post(`/projects/${projectId}/start`)
      currentProject.value = response.data
      
      const idx = projects.value.findIndex(p => p.id === projectId)
      if (idx >= 0) {
        projects.value[idx] = response.data
      }
      
      return response.data
    } finally {
      loading.value = false
    }
  }

  // Start manual step
  async function startStep(projectId: number, step: number) {
    loading.value = true
    try {
      const response = await api.post(`/projects/${projectId}/step/${step}`)
      currentProject.value = response.data
      return response.data
    } finally {
      loading.value = false
    }
  }

  // Update project
  async function updateProject(projectId: number, data: Partial<Project>) {
    loading.value = true
    try {
      const response = await api.put(`/projects/${projectId}`, data)
      currentProject.value = response.data
      
      const idx = projects.value.findIndex(p => p.id === projectId)
      if (idx >= 0) {
        projects.value[idx] = response.data
      }
      
      return response.data
    } finally {
      loading.value = false
    }
  }

  // Delete project
  async function deleteProject(projectId: number) {
    loading.value = true
    try {
      await api.delete(`/projects/${projectId}`)
      projects.value = projects.value.filter(p => p.id !== projectId)
      if (currentProject.value?.id === projectId) {
        currentProject.value = null
      }
      return true
    } finally {
      loading.value = false
    }
  }

  // Cancel project
  async function cancelProject(projectId: number) {
    loading.value = true
    try {
      await api.post(`/projects/${projectId}/cancel`)
      await fetchProject(projectId)
      return true
    } finally {
      loading.value = false
    }
  }

  // Update script
  async function updateScript(projectId: number, scriptJson: any) {
    try {
      const response = await api.put(`/projects/${projectId}/script`, {
        script_json: scriptJson
      })
      return response.data
    } catch (error) {
      console.error('Failed to update script:', error)
      throw error
    }
  }

  // Update scene
  async function updateScene(projectId: number, sceneId: number, data: Partial<Scene>) {
    try {
      const response = await api.put(`/projects/${projectId}/scenes/${sceneId}`, data)
      
      // Update in current project
      if (currentProject.value) {
        const idx = currentProject.value.scenes.findIndex(s => s.id === sceneId)
        if (idx >= 0) {
          currentProject.value.scenes[idx] = response.data
        }
      }
      
      return response.data
    } catch (error) {
      console.error('Failed to update scene:', error)
      throw error
    }
  }

  // Create scene
  async function createScene(projectId: number, data: {
    idx: number
    start_time: number
    duration_s: number
    description: string
    image_prompt: string
    video_prompt: string
  }) {
    try {
      const response = await api.post(`/projects/${projectId}/scenes`, data)
      
      // Refresh project to get updated scenes
      await fetchProject(projectId)
      
      return response.data
    } catch (error) {
      console.error('Failed to create scene:', error)
      throw error
    }
  }

  // Delete scene
  async function deleteScene(projectId: number, sceneId: number) {
    try {
      await api.delete(`/projects/${projectId}/scenes/${sceneId}`)
      
      // Refresh project to get updated scenes
      await fetchProject(projectId)
      
      return true
    } catch (error) {
      console.error('Failed to delete scene:', error)
      throw error
    }
  }

  // Validate script and start image generation (manual mode)
  async function validateScript(projectId: number) {
    loading.value = true
    try {
      const response = await api.post(`/projects/${projectId}/script/validate`)
      
      // Refresh project
      await fetchProject(projectId)
      
      return response.data
    } catch (error) {
      console.error('Failed to validate script:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Regenerate scene image in manual mode (with limit)
  async function regenerateSceneImageManual(projectId: number, sceneId: number, newPrompt: string) {
    loading.value = true
    try {
      const formData = new FormData()
      // Always send the prompt - this is the English prompt that was edited by the user
      formData.append('new_prompt', newPrompt)
      
      const response = await api.post(
        `/projects/${projectId}/scenes/${sceneId}/regenerate-image-manual`,
        formData
      )
      
      // Refresh project
      await fetchProject(projectId)
      
      return response.data
    } catch (error) {
      console.error('Failed to regenerate image:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Upload replacement image for a scene
  async function uploadSceneImage(projectId: number, sceneId: number, file: File) {
    loading.value = true
    try {
      const formData = new FormData()
      formData.append('image', file)
      
      const response = await api.post(
        `/projects/${projectId}/scenes/${sceneId}/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      
      // Refresh project
      await fetchProject(projectId)
      
      return response.data
    } catch (error) {
      console.error('Failed to upload image:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Validate images and start video generation (manual mode)
  async function validateImages(projectId: number) {
    loading.value = true
    try {
      const response = await api.post(`/projects/${projectId}/images/validate`)
      
      // Refresh project
      await fetchProject(projectId)
      
      return response.data
    } catch (error) {
      console.error('Failed to validate images:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Regenerate scene video in manual mode (with limit)
  async function regenerateSceneVideoManual(projectId: number, sceneId: number, newPrompt: string | null) {
    loading.value = true
    try {
      const formData = new FormData()
      if (newPrompt) {
        formData.append('new_prompt', newPrompt)
      }
      
      const response = await api.post(
        `/projects/${projectId}/scenes/${sceneId}/regenerate-video-manual`,
        formData
      )
      
      // Refresh project
      await fetchProject(projectId)
      
      return response.data
    } catch (error) {
      console.error('Failed to regenerate video:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Swap two scenes positions
  async function swapScenes(projectId: number, sceneAId: number, sceneBId: number) {
    loading.value = true
    try {
      const formData = new FormData()
      formData.append('scene_a_id', sceneAId.toString())
      formData.append('scene_b_id', sceneBId.toString())
      
      const response = await api.post(
        `/projects/${projectId}/scenes/swap`,
        formData
      )
      
      // Refresh project
      await fetchProject(projectId)
      
      return response.data
    } catch (error) {
      console.error('Failed to swap scenes:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Validate videos and start merge (manual mode)
  async function validateVideos(projectId: number) {
    loading.value = true
    try {
      const response = await api.post(`/projects/${projectId}/videos/validate`)
      
      // Refresh project
      await fetchProject(projectId)
      
      return response.data
    } catch (error) {
      console.error('Failed to validate videos:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Regenerate scene image
  async function regenerateSceneImage(projectId: number, sceneId: number) {
    try {
      const response = await api.post(`/projects/${projectId}/scenes/${sceneId}/regenerate-image`)
      return response.data
    } catch (error) {
      console.error('Failed to regenerate image:', error)
      throw error
    }
  }

  // Regenerate scene video
  async function regenerateSceneVideo(projectId: number, sceneId: number) {
    try {
      const response = await api.post(`/projects/${projectId}/scenes/${sceneId}/regenerate-video`)
      return response.data
    } catch (error) {
      console.error('Failed to regenerate video:', error)
      throw error
    }
  }

  // Poll project status
  let pollInterval: number | null = null
  
  function startPolling(projectId: number, intervalMs = 3000) {
    stopPolling()
    pollInterval = window.setInterval(() => {
      fetchProject(projectId)
    }, intervalMs)
  }
  
  function stopPolling() {
    if (pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
    }
  }

  // Check Director access for project
  async function checkDirectorAccess(projectId: number) {
    try {
      const response = await api.get(`/projects/${projectId}/check-director`)
      return response.data
    } catch (error) {
      console.error('Failed to check director access:', error)
      return { has_director: false, can_download: false }
    }
  }

  // Download project assets (Director only)
  async function downloadAssets(projectId: number) {
    try {
      const response = await api.post(`/projects/${projectId}/download-assets`)
      return response.data
    } catch (error) {
      console.error('Failed to create assets download:', error)
      throw error
    }
  }

  return {
    projects,
    currentProject,
    loading,
    total,
    page,
    activeProjects,
    completedProjects,
    checkHasActiveProject,
    fetchProjects,
    fetchProject,
    createProject,
    startProject,
    startStep,
    updateProject,
    deleteProject,
    cancelProject,
    updateScript,
    updateScene,
    createScene,
    deleteScene,
    validateScript,
    regenerateSceneImageManual,
    uploadSceneImage,
    validateImages,
    regenerateSceneVideoManual,
    swapScenes,
    validateVideos,
    regenerateSceneImage,
    regenerateSceneVideo,
    startPolling,
    stopPolling,
    checkDirectorAccess,
    downloadAssets
  }
})
